/*
 * Summary page (risk dashboard home).
 *
 * Executive at-a-glance view: org risk dial, benchmark position, 6-month
 * trend chart, AI-composed risk narrative, and the 5-factor strip with
 * drill affordance. When the shell scope is set (department / location)
 * the org-level cards collapse and only scope-filtered widgets render.
 *
 * Calculations: every card reads precomputed exports from riskScore.js
 * (ORG_SCORE, ORG_BAND, ORG_DELTA, ORG_TREND, BENCHMARK, ORG_FACTOR_STATS);
 * the AI risk narrative (AICard) is static product copy. The page itself does
 * no aggregation.
 *
 * Postgres mapping:
 *   - source rows: `org_risk_snapshot`, `factor_stats`, `org_risk_trend`,
 *     `industry_benchmarks` (all refreshed nightly).
 *
 * Integration hook: `riskApi.useOrgRiskSummaryQuery()` returning
 * { score, band, delta, trend, factors, benchmark, summary }; cache 1h.
 * Widget visibility via useWidgetVisibility('summary').
 */
import React from 'react';
import { Box, Grid, Button, Alert } from '@mui/material';
import { motion, AnimatePresence } from 'motion/react';
import RiskDialCard from './RiskDialCard';
import AICard, { useAICardQueries } from './AICard';
import RiskTrendChart, { useRiskTrendQuery } from './RiskTrendChart';
import BenchmarkPositionCard, { useRiskBenchmarkQuery } from './BenchmarkPositionCard';
import DismissableCard from './DismissableCard';
import ActiveFilters from '../ActiveFilters';
import FactorStrip from './FactorStrip';
import MetaRiskLeagueCard, { useLeaguesQuery } from './MetaRiskLeagueCard';
import WidgetState from '../WidgetState';
import SectionTitle from '../SectionTitle';
import useDashboardData from '../../hooks/useDashboardData';

const PERIOD_PHRASE = {
    Today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days',
    '12m': 'Last 12 months', 'This month': 'This month', 'Last month': 'Last month',
};

// Risk scores are a fixed rolling 6-month window, so the shared date filter never
// changes this page. Once the viewer has moved off the 6m default on another tab,
// say so rather than let it look like the filter was ignored (BD-1003).
export function riskWindowNotice({ period, startDate, endDate }) {
    if (period === '6m') return null;
    const label = PERIOD_PHRASE[period] || (startDate && endDate ? `${startDate} to ${endDate}` : null);
    if (!label) return null;
    return `Risk scores always cover a rolling 6-month window, so the date filter does not change this page. Your "${label}" filter still applies on Simulations, Courses and Users.`;
}

export default function SummaryPage({ go, drill, scope, removeScope, period, setPeriod, startDate, setStartDate, endDate, setEndDate, vis, dismiss, toggle, restoreAll, allWidgets, hidden }) {
    const isScoped = scope && Object.keys(scope).length > 0;
    const isUserScoped = isScoped && !!scope.user;
    // A tenancy scope (BD-565/629) redefines WHICH org(s) the page covers, so the
    // org-level cards stay meaningful (just pooled) under it - only a within-org
    // scope (department/location/.../user) makes them nonsensical and hides them.
    const isSubOrgScoped = !!scope && Object.keys(scope).some(
        (k) => k !== 'tenancy' && scope[k] && (!Array.isArray(scope[k]) || scope[k].length > 0));
    // Server scope forwarded to the summary widgets that hit the dashboard
    // endpoint, so the Tenancy filter (scope.tenancy, number[]) pools the
    // selected child tenancies for every server-backed card the same way the
    // Sims / Courses / Users pages do (BD-565/629). Tenancy ids stay Numbers.
    const sc = scope || {};
    const normScope = (v) => (v == null ? undefined : Array.isArray(v) ? (v.length ? v : undefined) : [v]);
    const serverScope = { department: normScope(sc.department), location: normScope(sc.location), city: normScope(sc.city), country: normScope(sc.country), state: normScope(sc.state), company: normScope(sc.company), user: normScope(sc.user), tenancy: normScope(sc.tenancy) };
    const hasServerScope = Object.values(serverScope).some(Boolean);
    const scopeArg = hasServerScope ? serverScope : undefined;
    // Org risk score + factor means. Skipped under a within-org scope (it is the
    // org-wide baseline the scoped cards replace), but under a tenancy-only scope
    // it fires WITH scope.tenancy so the dial + factor means pool the selected
    // child tenancies (BD-565/629).
    const riskQ = useDashboardData({ metric: 'simulations', aggregation: 'risk', scope: scopeArg }, { skip: isSubOrgScoped });
    const riskAgg = (!isSubOrgScoped && riskQ.data?.risk) || null;
    const allHidden = hidden.length === allWidgets.length;
    const windowNotice = riskWindowNotice({ period, startDate, endDate });

    const metaLeagueVisible = !isSubOrgScoped && vis('meta-risk-league');
    const riskSummaryVisible = vis('risk-dial') || vis('risk-trend') || vis('benchmark-position') || vis('ai-summary') || metaLeagueVisible;
    const scoreDriversVisible = !isUserScoped && vis('factor-strip');

    // BD-1196: the cards own these queries; same args here = the same cached requests,
    // read for their loading state so each card can show a skeleton.
    const benchQ = useRiskBenchmarkQuery(scopeArg, { skip: !((!isSubOrgScoped && vis('benchmark-position')) || scoreDriversVisible) });
    const leaguesQ = useLeaguesQuery(scopeArg, { skip: !metaLeagueVisible });
    const trendQ = useRiskTrendQuery(scopeArg, { skip: isSubOrgScoped || !vis('risk-trend') });
    const aiQs = useAICardQueries(scopeArg, { skip: isSubOrgScoped || !vis('ai-summary') });

    return (
        <Box sx={{ p: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* Applied scope filters sit at the very top, directly under the shell's
                tenancy filter - not buried below the (tall) risk summary further down. */}
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />

            {windowNotice && (
                <Alert severity="info" variant="outlined" data-testid="risk-window-notice" sx={{ fontSize: 12, py: 0, color: 'text.primary' }}>
                    {windowNotice}
                </Alert>
            )}

            {/* Risk summary, hidden when scoped, these are org-level only.
                Two rows grouped by intent: snapshot reads (dial + benchmark
                rail) sit above narrative reads (trend + AI summary). Each
                row collapses cleanly if its members are hidden. */}
            {!isSubOrgScoped && riskSummaryVisible && (
                <>
                    <SectionTitle>Risk summary</SectionTitle>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(vis('risk-dial') || vis('benchmark-position') || metaLeagueVisible) && (() => {
                            // First row: risk dial / benchmark / league at 0.2 / 0.5 / 0.3.
                            // MUI Grid only accepts integer 1-12 spans, so use a CSS grid
                            // with fractional template columns so the layout matches
                            // the requested ratio exactly.
                            const cols = [
                                vis('risk-dial')        && { key: 'risk-dial',        flex: 2, node: <WidgetState queries={[riskQ]} fill><DismissableCard id="risk-dial"        sx={{ height: '100%' }}><RiskDialCard go={go} orgScore={riskAgg && typeof riskAgg.orgScore === 'number' ? riskAgg.orgScore : undefined} scope={scopeArg} /></DismissableCard></WidgetState> },
                                vis('benchmark-position') && { key: 'benchmark',      flex: 5, node: <WidgetState queries={[riskQ, benchQ]} fill><DismissableCard id="benchmark-position" sx={{ height: '100%' }}><BenchmarkPositionCard orgScore={riskAgg && typeof riskAgg.orgScore === 'number' ? riskAgg.orgScore : undefined} scope={scopeArg} /></DismissableCard></WidgetState> },
                                metaLeagueVisible        && { key: 'meta-risk-league', flex: 3, node: <WidgetState queries={[leaguesQ]} fill><DismissableCard id="meta-risk-league"  sx={{ height: '100%' }}><MetaRiskLeagueCard drill={drill} scope={scopeArg} /></DismissableCard></WidgetState> },
                            ].filter(Boolean);
                            const template = cols.map((c) => `${c.flex}fr`).join(' ');
                            return (
                                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: template }, alignItems: 'stretch' }}>
                                    {cols.map((c) => (
                                        <Box key={c.key} sx={{ display: 'flex', minWidth: 0 }}>
                                            <Box sx={{ flex: 1, display: 'flex' }}>{c.node}</Box>
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })()}
                        {(vis('risk-trend') || vis('ai-summary')) && (
                            <Grid container spacing={1} alignItems="stretch">
                                {vis('risk-trend') && (
                                    <Grid item xs={12} md={vis('ai-summary') ? 8 : 12} sx={{ display: 'flex', flexDirection: 'column' }} key="risk-trend">
                                        <WidgetState queries={[trendQ]} fill>
                                            <DismissableCard id="risk-trend" sx={{ height: '100%' }}>
                                                <RiskTrendChart scope={scopeArg} />
                                            </DismissableCard>
                                        </WidgetState>
                                    </Grid>
                                )}
                                {vis('ai-summary') && (
                                    <Grid item xs={12} md={vis('risk-trend') ? 4 : 12} sx={{ display: 'flex', flexDirection: 'column' }} key="ai-summary">
                                        <WidgetState queries={[riskQ, ...aiQs]} fill>
                                            <DismissableCard id="ai-summary" sx={{ height: '100%' }}>
                                                <AICard go={go} risk={riskAgg} scope={scopeArg} />
                                            </DismissableCard>
                                        </WidgetState>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Box>
                </>
            )}

            {/* Score drivers, the five factors that actually make up the risk
                score, shown as explorables so users investigate what's driving
                their score rather than browsing generic activity stats. Hidden
                on user-scope: the per-user breakdown modal does the same job
                with more precision when one user is in focus. */}
            {scoreDriversVisible && (
                <>
                    <SectionTitle>Score drivers</SectionTitle>
                    <WidgetState queries={[riskQ, benchQ]}>
                        <DismissableCard id="factor-strip">
                            <FactorStrip drill={drill} risk={riskAgg} scope={scopeArg} />
                        </DismissableCard>
                    </WidgetState>
                </>
            )}

            {/* Empty state */}
            <AnimatePresence>
                {allHidden && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                        <Box sx={{ textAlign: 'center', py: 10, color: 'text.disabled' }}>
                            <Box sx={{ fontSize: 13, mb: 1 }}>All cards hidden</Box>
                            <Button size="small" variant="outlined" onClick={restoreAll} sx={{ fontSize: 11, textTransform: 'none' }}>
                                Customise layout
                            </Button>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

        </Box>
    );
}
